import {
  type QueryKey,
  type UseInfiniteQueryOptions,
  useInfiniteQuery,
} from "@tanstack/react-query"
import { useMemo } from "react"

import { extractPageNumberFromNextPageRoute } from "@/utils/extractPageNumberFromNextPageRoute"
import type { GetCollectionData } from "@/utils/infiniteQueryDataUtils"
import { isNullOrUndefined } from "@/utils/ts-utils"

export const useAppInfiniteQuery = <
  TParams extends { page?: number },
  TData,
  TError = unknown,
  TQueryKey extends QueryKey = QueryKey,
>(
  getData: (params: TParams) => Promise<GetCollectionData<TData>>,
  getQueryKey: (params: TParams) => TQueryKey,
  params: TParams = {} as TParams,
  options?: Omit<
    UseInfiniteQueryOptions<
      GetCollectionData<TData>,
      TError,
      GetCollectionData<TData>,
      GetCollectionData<TData>,
      TQueryKey
    >,
    "queryKey"
  >
) => {
  const infiniteResult = useInfiniteQuery<
    GetCollectionData<TData>,
    TError,
    GetCollectionData<TData>,
    TQueryKey
  >({
    queryKey: getQueryKey(params),
    queryFn: ({ pageParam = "" }) => {
      const nextPage =
        typeof pageParam === "string" ? extractPageNumberFromNextPageRoute(pageParam) : undefined

      return getData({ ...params, page: nextPage })
    },
    getNextPageParam: lastPage => lastPage["hydra:view"]?.["hydra:next"],
    ...options,
  })

  const extra = useMemo(() => {
    const rawData = infiniteResult.data
    if (isNullOrUndefined(rawData))
      return { rawData: undefined, totalCount: undefined, flatData: [] }

    const rawPage0 = rawData?.pages?.[0] ?? undefined
    const dataObj = isNullOrUndefined(rawPage0)
      ? (rawData as unknown as GetCollectionData<TData>)
      : rawPage0

    const totalCount = dataObj?.["hydra:totalItems"] ?? 0
    const flatData = isNullOrUndefined(rawPage0)
      ? (dataObj?.["hydra:member"] ?? [])
      : (rawData?.pages?.flatMap(page => page?.["hydra:member"]) ?? [])

    return { rawData, totalCount, flatData }
  }, [infiniteResult])

  return { ...infiniteResult, ...extra }
}
